import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Truck, Shield, RefreshCw } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { Skeleton } from '../components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data if needed and fetch products
        await axios.post(`${API}/seed`);
        const [productsRes, catsRes] = await Promise.all([
          axios.get(`${API}/products?featured=true&limit=8`),
          axios.get(`${API}/categories`)
        ]);
        setFeaturedProducts(productsRes.data);
        setCategories(catsRes.data);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="bg-black" data-testid="home-page">
      {/* Hero Section with Fire Gradient */}
      <section className="relative min-h-[90vh] fire-gradient flex items-center" data-testid="hero-section">
        <div className="max-w-[1800px] mx-auto px-4 md:px-8 w-full py-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left - Typography */}
            <div className="order-2 lg:order-1 space-y-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_print-store-6/artifacts/i6m0cwtm_Logo%20%281%29.png" 
                alt="PosterFire" 
                className="h-20 md:h-28 w-auto animate-fade-in"
              />
              <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl leading-none text-white drop-shadow-2xl animate-fade-in">
                IGNITE YOUR<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-red-600">WALLS</span>
              </h1>
              <p className="text-lg md:text-xl text-white/80 max-w-md animate-fade-in" style={{ animationDelay: '0.2s' }}>
                Premium posters that transform spaces. From cinema legends to modern abstracts. Print on demand in any size.
              </p>
              <div className="flex flex-wrap gap-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <Link to="/shop" className="bg-black text-white py-4 px-8 font-bold uppercase tracking-wider hover:bg-fire transition-colors inline-flex items-center gap-2" data-testid="hero-shop-btn">
                  Shop Now <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/shop?featured=true" className="border-2 border-white text-white py-4 px-8 font-bold uppercase tracking-wider hover:bg-white hover:text-black transition-colors" data-testid="hero-featured-btn">
                  Featured
                </Link>
              </div>
            </div>

            {/* Right - Featured Image */}
            <div className="order-1 lg:order-2 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1513519245088-0e12902e35ca?w=800"
                  alt="Modern interior with art posters"
                  className="w-full aspect-[4/5] object-cover shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-fire text-white p-6 hidden md:block fire-glow">
                  <p className="font-heading text-4xl font-bold">50K+</p>
                  <p className="text-sm uppercase tracking-wider">Posters</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bar */}
      <section className="bg-black py-12 border-y border-white/10">
        <div className="max-w-[1800px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-center gap-4">
              <Truck className="w-8 h-8 text-fire" />
              <div>
                <p className="font-heading font-bold text-white uppercase tracking-tight">Free Shipping</p>
                <p className="text-sm text-white/50">On orders over $50</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Shield className="w-8 h-8 text-fire" />
              <div>
                <p className="font-heading font-bold text-white uppercase tracking-tight">Quality Guarantee</p>
                <p className="text-sm text-white/50">Museum-grade prints</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <RefreshCw className="w-8 h-8 text-fire" />
              <div>
                <p className="font-heading font-bold text-white uppercase tracking-tight">Easy Returns</p>
                <p className="text-sm text-white/50">30-day return policy</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 bg-gradient-to-b from-black to-neutral-900" data-testid="categories-section">
        <div className="max-w-[1800px] mx-auto px-4 md:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="text-sm font-medium uppercase tracking-widest text-fire">Browse By</span>
              <h2 className="font-heading text-4xl md:text-5xl text-white mt-2">Categories</h2>
            </div>
            <Link to="/shop" className="text-sm font-medium uppercase tracking-widest text-white/70 hover:text-fire transition-colors hidden md:flex items-center gap-2">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.slice(0, 5).map((cat) => (
              <Link
                key={cat.id}
                to={`/shop?category=${cat.id}`}
                className="group relative overflow-hidden aspect-[3/4] bg-gradient-to-br from-fire/20 to-black"
                data-testid={`category-${cat.id}`}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                <div className="absolute bottom-0 left-0 p-4 md:p-6">
                  <h3 className="font-heading text-white text-lg md:text-xl font-bold uppercase tracking-tight group-hover:text-fire transition-colors">
                    {cat.name}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 fire-gradient" data-testid="featured-section">
        <div className="max-w-[1800px] mx-auto px-4 md:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="text-sm font-medium uppercase tracking-widest text-black">Curated Selection</span>
              <h2 className="font-heading text-4xl md:text-5xl text-white drop-shadow-lg mt-2">Featured Posters</h2>
            </div>
            <Link to="/shop?featured=true" className="text-sm font-medium uppercase tracking-widest text-white hover:text-black transition-colors hidden md:flex items-center gap-2">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <Skeleton className="aspect-[3/4] w-full bg-black/30" />
                  <Skeleton className="h-4 w-20 bg-black/30" />
                  <Skeleton className="h-5 w-full bg-black/30" />
                  <Skeleton className="h-6 w-16 bg-black/30" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product, i) => (
                <div key={product.id} className="animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                  <ProductCard product={product} index={i} />
                </div>
              ))}
            </div>
          )}

          <div className="mt-12 text-center md:hidden">
            <Link to="/shop?featured=true" className="inline-block bg-black text-white py-4 px-8 font-bold uppercase tracking-wider hover:bg-white hover:text-black transition-colors">
              View All Featured
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black">
        <div className="max-w-[1800px] mx-auto px-4 md:px-8">
          <div className="bg-gradient-to-r from-yellow-600 via-orange-600 to-red-700 p-8 md:p-16 text-center">
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-4">Ready to Transform Your Space?</h2>
            <p className="text-white/90 max-w-2xl mx-auto mb-8">
              Join thousands of art lovers who've discovered their perfect poster. New arrivals weekly.
            </p>
            <Link to="/shop" className="inline-block bg-black text-white py-4 px-8 font-bold uppercase tracking-wider hover:bg-white hover:text-black transition-colors">
              Start Shopping
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
